import React, { useState, useEffect } from 'react';
import { PageHeader } from '../../components/ui/page-header';
import { Card, CardHeader, CardTitle, CardContent, Button } from '../../components/ui';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

interface User {
  id: number;
  email: string;
  is_admin: boolean;
  permissions: string[];
}

const AVAILABLE_PERMISSIONS = [
  { id: 'database_manage', label: '数据库管理' },
  { id: 'user_manage', label: '用户管理' },
  // Add more permissions as needed
];

const UsersPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUserFormOpen, setIsUserFormOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    is_admin: false,
    permissions: [] as string[]
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/users', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      setUsers(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateUser = () => {
    setSelectedUser(null);
    setFormData({
      email: '',
      password: '',
      is_admin: false,
      permissions: []
    });
    setIsUserFormOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      password: '', // Don't populate password for security
      is_admin: user.is_admin,
      permissions: user.permissions
    });
    setIsUserFormOpen(true);
  };

  const handleDeleteUser = async (userId: number) => {
    if (window.confirm('确定要删除这个用户吗？')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/users/${userId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          fetchUsers();
        } else {
          const error = await response.json();
          throw new Error(error.error || '删除失败');
        }
      } catch (err) {
        console.error('Error deleting user:', err);
        alert('删除失败，请重试');
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePermissionChange = (permission: string) => {
    setFormData(prev => {
      const permissions = [...prev.permissions];
      if (permissions.includes(permission)) {
        return { ...prev, permissions: permissions.filter(p => p !== permission) };
      } else {
        return { ...prev, permissions: [...permissions, permission] };
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const url = selectedUser ? `/api/users/${selectedUser.id}` : '/api/users';
      const method = selectedUser ? 'PUT' : 'POST';
      
      // Only include password if it's provided (for editing users)
      const userData = {
        ...formData,
        password: formData.password || undefined
      };
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || '保存失败');
      }
      
      fetchUsers();
      setIsUserFormOpen(false);
    } catch (error) {
      console.error('Error saving user:', error);
      alert('保存失败，请重试');
    }
  };

  // If user doesn't have user_manage permission, show access denied
  if (!hasPermission('user_manage')) {
    return (
      <div className="container mx-auto p-4">
        <PageHeader title="用户管理" />
        <Card className="mt-6">
          <CardContent className="p-6">
            <div className="text-center text-red-500">
              <p>您没有权限访问此页面</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 space-y-6">
      <PageHeader title="用户管理" description="管理系统用户及权限" />
      
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>用户列表</CardTitle>
          <Button
            onClick={handleCreateUser}
            className="bg-blue-500 hover:bg-blue-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            添加用户
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-4">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
              <p className="mt-2">加载中...</p>
            </div>
          ) : error ? (
            <div className="text-red-500 text-center py-4">
              {error}
              <Button
                onClick={fetchUsers}
                className="ml-2 bg-blue-500 hover:bg-blue-600"
              >
                重试
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="p-2 text-left">邮箱</th>
                    <th className="p-2 text-left">角色</th>
                    <th className="p-2 text-left">权限</th>
                    <th className="p-2 text-center">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-4">暂无用户</td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user.id} className="border-b hover:bg-gray-50">
                        <td className="p-2">{user.email}</td>
                        <td className="p-2">{user.is_admin ? '管理员' : '普通用户'}</td>
                        <td className="p-2">
                          {user.is_admin ? '全部权限' : 
                            user.permissions.length ? 
                              user.permissions.map(p => 
                                AVAILABLE_PERMISSIONS.find(ap => ap.id === p)?.label || p
                              ).join(', ') 
                              : '无权限'
                          }
                        </td>
                        <td className="p-2 text-center">
                          <div className="flex justify-center space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditUser(user)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(user.id)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* User Form Dialog */}
      <Dialog open={isUserFormOpen} onOpenChange={setIsUserFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedUser ? '编辑用户' : '添加用户'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">邮箱</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">密码{selectedUser && ' (留空则不修改)'}</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required={!selectedUser}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_admin"
                checked={formData.is_admin}
                onChange={handleInputChange}
                id="is_admin"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_admin" className="ml-2 block text-sm text-gray-900">
                管理员 (具有所有权限)
              </label>
            </div>
            
            {!formData.is_admin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">权限</label>
                <div className="space-y-2">
                  {AVAILABLE_PERMISSIONS.map(permission => (
                    <div key={permission.id} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`perm-${permission.id}`}
                        checked={formData.permissions.includes(permission.id)}
                        onChange={() => handlePermissionChange(permission.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`perm-${permission.id}`} className="ml-2 block text-sm text-gray-900">
                        {permission.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <DialogFooter className="flex justify-end space-x-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsUserFormOpen(false)}
              >
                取消
              </Button>
              <Button
                type="submit"
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                保存
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage; 